import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as Sentry from '@sentry/browser';
import moment from 'moment';

import FirebaseContext from '../context/FirebaseContext';
import UpsellConfig from '../CoachTools/Model/UpsellConfig';
import { DateFormat } from '../utils/date';
import { toMomentUnit } from '../utils/durationUnit';
import { EventTypes } from '../utils/events';
import { upsellAnswerParam, upsellArmParam } from '../utils/onboarding';
import { useChallengeUpsellPreEnrollEnabled } from '../utils/postHog';
import {
  LandingUpsellAnswer,
  UpsellArm,
  UpsellSource,
  resolveUpsellArm,
} from '../utils/upsellExperiment';
import useComponentMounted from './useComponentMounted';
import useLogger from './useLogger';

/**
 * The day a challenge bought today ends: its own start when that is still ahead, else today, plus its term in its
 * unit. Only what the 1:1 contract's terms name as its start — the 1:1 actually starts when the challenge's
 * subscription ends.
 *
 * @param {Object} contractDoc the challenge contract being bought
 * @returns {string} the end day, 'YYYY-MM-DD'
 */
const getChallengeEndDate = (contractDoc) => {
  const today = moment().startOf('day');
  const contractStart = moment(contractDoc.startDate, DateFormat.DEFAULT_DATE_FORMAT, true);
  const start = contractStart.isValid() && contractStart.isAfter(today) ? contractStart : today;
  return start
    .add(contractDoc.minSubscriptionMonths || 1, toMomentUnit(contractDoc.durationUnit))
    .format(DateFormat.DEFAULT_DATE_FORMAT);
};

/**
 * The coach's 1:1 upsell around a challenge bought on Avida checkout, for the arm of the upsell A/B test the visitor
 * is in (utils/upsellExperiment):
 *
 * - LANDING, accepted on the landing page: `landing.isShown` once the offer is known (the client is signed in),
 *   `landing.isAdded` while the client keeps it; signed up after payment by `signUpAfterPayment`.
 * - CONTROL, the default: `postPayment.isAvailable` once the offer is known; the client answers it after paying
 *   with `postPayment.accept` / `postPayment.decline`. `postPayment.isSettled` says whether that is known yet, so
 *   the checkout doesn't move on before an offer it would have shown.
 *
 * Only a paid challenge sold on Avida checkout, for a coach rolled out to the upsell, takes part: a free challenge
 * saves no card to charge the 1:1 on, and Stripe Hosted Checkout saves none for later. The offer (product, price,
 * commitment) can only be read once the client is signed in, so before that `offer` is null.
 *
 * Signing up is what an accepted upsell does in the app: the 1:1 contract is created against the client's own
 * challenge contract (createUpsellContract) and accepted against its subscription (acceptContract), so it starts, and
 * is first charged, when the challenge ends. It happens once per purchase and a failure never undoes the challenge.
 *
 * @param {Object} params
 * @param {Object} [params.contractDoc] the contract being bought (a UserContract)
 * @param {boolean} params.isAvidaCheckout whether it is being sold on Avida checkout
 * @param {boolean} params.isSignedIn whether the client is signed in
 */
const useCheckoutUpsell = ({ contractDoc, isAvidaCheckout, isSignedIn }) => {
  const { firebase: { remote } } = useContext(FirebaseContext);
  const { logEvent } = useLogger();
  const isComponentMountedRef = useComponentMounted();
  const isRolledOut = useChallengeUpsellPreEnrollEnabled(contractDoc?.coach);

  // `undefined` while not known (signed out, or loading); `null` once known that there is none.
  const [loadedOffer, setLoadedOffer] = useState(undefined);
  const offer = loadedOffer ?? null;
  const [isLandingKept, setIsLandingKept] = useState(true);
  const hasSignedUpRef = useRef(false);
  const [hasAddedCoaching, setHasAddedCoaching] = useState(false);
  // Sources whose offer was already reported shown: once per visitor, so the test's exposure counts stay true.
  const shownSourcesRef = useRef(new Set());

  const arm = resolveUpsellArm(upsellArmParam);
  const isTakingPart = !!contractDoc?.challengeId && !contractDoc.isFree && isAvidaCheckout && isRolledOut;
  const isLandingAccepted = isTakingPart && arm === UpsellArm.LANDING
    && upsellAnswerParam === LandingUpsellAnswer.ACCEPTED;
  const needsOffer = isLandingAccepted || (isTakingPart && arm === UpsellArm.CONTROL);

  useEffect(() => {
    if (!needsOffer || !isSignedIn) {
      setLoadedOffer(undefined);
      return;
    }
    const loadOffer = async () => {
      try {
        const upsellConfig = await UpsellConfig.getByCoachId(contractDoc.coach);
        const checkoutOffer = upsellConfig.getCheckoutPreEnrollOffer(contractDoc.baseContractId || contractDoc.id);
        // The server bills the 1:1 in the client's own currency and refuses any other.
        const isClientCurrency = checkoutOffer?.currency?.toLowerCase() === contractDoc.currency?.toLowerCase();
        if (isComponentMountedRef.current) {
          setLoadedOffer(isClientCurrency ? checkoutOffer : null);
        }
      } catch (error) {
        Sentry.captureException(error, { extra: { contractId: contractDoc.id, context: 'useCheckoutUpsell' } });
        if (isComponentMountedRef.current) {
          setLoadedOffer(null);
        }
      }
    };
    loadOffer();
  }, [needsOffer, isSignedIn, contractDoc, isComponentMountedRef]);

  const logUpsellEvent = useCallback((eventType, source) => {
    logEvent(eventType, {
      arm,
      source,
      contractId: contractDoc?.id,
      coachId: contractDoc?.coach,
      ...(offer && { productId: offer.productId, monthlyPriceInCents: offer.monthlyPriceInCents }),
    });
  }, [logEvent, arm, contractDoc, offer]);

  const logShown = useCallback((source) => {
    if (shownSourcesRef.current.has(source)) {
      return;
    }
    shownSourcesRef.current.add(source);
    logUpsellEvent(EventTypes.UPSELL_OFFER_SHOWN, source);
  }, [logUpsellEvent]);

  /**
   * Signs the client up for the 1:1. Never throws.
   *
   * @param {Object} subscription the challenge's Stripe subscription, as the purchase returned it
   * @param {string} source one of UpsellSource
   * @returns {Promise<boolean>} whether the client is now signed up
   */
  const signUp = useCallback(async (subscription, source) => {
    if (!offer || hasSignedUpRef.current) {
      return false;
    }
    hasSignedUpRef.current = true;
    // The client's own challenge contract, created from the reusable one at purchase.
    const baseUpsellContractId = subscription?.metadata?.contractId;
    try {
      if (!baseUpsellContractId) {
        throw new Error('The challenge subscription names no contract');
      }
      const createResponse = await remote('createUpsellContract', {
        baseUpsellContractId,
        startDate: getChallengeEndDate(contractDoc),
        upsellSource: source,
      });
      const { contractId: upsellContractId, error: createError } = await createResponse.json();
      if (createError || !upsellContractId) {
        throw new Error(`createUpsellContract failed: ${createError || 'no contract'}`);
      }
      const acceptResponse = await remote('acceptContract', { contractId: upsellContractId });
      const { success, error: acceptError } = await acceptResponse.json();
      if (!success) {
        throw new Error(`acceptContract failed: ${acceptError || 'not accepted'}`);
      }
      logUpsellEvent(EventTypes.UPSELL_OFFER_ACCEPTED, source);
      if (isComponentMountedRef.current) {
        setHasAddedCoaching(true);
      }
      return true;
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          contractId: contractDoc?.id, baseUpsellContractId, source, context: 'useCheckoutUpsell.signUp',
        },
      });
      return false;
    }
  }, [offer, remote, contractDoc, logUpsellEvent, isComponentMountedRef]);

  const isLandingAdded = isLandingAccepted && isLandingKept;
  // The landing answer acted on after payment; null until the payment is first reported.
  const afterPaymentRef = useRef(null);

  /**
   * Signs up a 1:1 accepted on the landing page and kept at checkout, or reports it declined when the client removed
   * it: the answer counts once, as it stood at payment, however often they removed it and added it back. The checkout
   * may report the payment more than once; a repeat waits for the first answer instead of acting again, so an offer
   * read only after payment, which the client never saw, signs nothing up.
   */
  const signUpAfterPayment = useCallback((subscription) => {
    if (!afterPaymentRef.current) {
      if (isLandingAdded) {
        afterPaymentRef.current = signUp(subscription, UpsellSource.LANDING);
      } else {
        if (isLandingAccepted && offer) {
          logUpsellEvent(EventTypes.UPSELL_OFFER_DECLINED, UpsellSource.LANDING);
        }
        afterPaymentRef.current = Promise.resolve(false);
      }
    }
    return afterPaymentRef.current;
  }, [isLandingAdded, isLandingAccepted, offer, signUp, logUpsellEvent]);

  const landing = useMemo(() => ({
    // Only with the offer to show: its price can't be read before sign-in, and a coach may have none.
    isShown: isLandingAccepted && !!offer,
    isAdded: isLandingAdded,
    remove: () => setIsLandingKept(false),
    addBack: () => setIsLandingKept(true),
    logShown: () => logShown(UpsellSource.LANDING),
  }), [isLandingAccepted, offer, isLandingAdded, logShown]);

  const isOfferedAfterPayment = isTakingPart && arm === UpsellArm.CONTROL;
  const postPayment = useMemo(() => ({
    isAvailable: isOfferedAfterPayment && !!offer,
    // Whether there is an offer is known: none is coming, or the coach's has been read.
    isSettled: !isOfferedAfterPayment || loadedOffer !== undefined,
    accept: (subscription) => signUp(subscription, UpsellSource.POST_PAYMENT),
    decline: () => logUpsellEvent(EventTypes.UPSELL_OFFER_DECLINED, UpsellSource.POST_PAYMENT),
    logShown: () => logShown(UpsellSource.POST_PAYMENT),
  }), [isOfferedAfterPayment, offer, loadedOffer, signUp, logUpsellEvent, logShown]);

  const challengeEndDate = contractDoc?.challengeId ? getChallengeEndDate(contractDoc) : null;

  return useMemo(() => ({
    arm,
    offer,
    challengeEndDate,
    landing,
    postPayment,
    signUpAfterPayment,
    // Whether the client is now signed up for the 1:1, from either arm.
    hasAddedCoaching,
  }), [arm, offer, challengeEndDate, landing, postPayment, signUpAfterPayment, hasAddedCoaching]);
};

export default useCheckoutUpsell;
export { getChallengeEndDate };
